﻿package devxertech.com.pages.services.chatbotDevelopment

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import devxertech.com.theme.SitePalette
import devxertech.com.utils.Constants
import devxertech.com.utils.Res
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private data class ProsDemo(val title: String, val description: String, val url: String, val image: String, val imageAlt: String)

private val prosDemos = listOf(
    ProsDemo("Immigration", "Help visitors with general inquiries, collect intake details and support consultation booking.", "https://devxerpros.com/demo/immigration", Res.Image.devxerpros_immigration, "Devxer Pros immigration demo page"),
    ProsDemo("Dental Practices", "Answer practice questions, gather appointment requests and hand inquiries to the team.", "https://devxerpros.com/demo/dental", Res.Image.devxerpros_dental, "Devxer Pros dental practice demo page"),
    ProsDemo("HVAC Companies", "Capture service details, flag urgent inquiries and prepare leads for your dispatch team.", "https://devxerpros.com/demo/hvac", Res.Image.devxerpros_hvac, "Devxer Pros HVAC company demo page")
)

@Composable
fun DevxerProsShowcase(breakpoint: Breakpoint, palette: SitePalette) {
    Section(attrs = {
        id("devxer-pros")
        attr("aria-labelledby", "devxer-pros-heading")
        style {
            width(100.percent)
            property("box-sizing", "border-box")
            padding(if (breakpoint <= Breakpoint.MD) 36.px else 60.px, if (breakpoint <= Breakpoint.MD) 20.px else 40.px)
            backgroundColor(palette.bodyBackgroundSecondary.rgb)
            color(palette.commonSecondaryButtonColor.rgb)
        }
    }) {
        Div(attrs = { style { property("max-width", "1100px"); property("margin", "0 auto") } }) {
            P(attrs = { style {
                margin(0.px, 0.px, 10.px); fontFamily(Constants.ROBOTO_FONT); fontSize(14.px)
                fontWeight(700); color(palette.primary.rgb); textAlign("center")
            } }) { Text("Built by Devxer Technologies") }
            H2(attrs = { id("devxer-pros-heading"); style {
                margin(0.px, 0.px, 16.px); fontFamily(Constants.ROBOTO_FONT)
                fontSize(if (breakpoint <= Breakpoint.MD) 25.px else 35.px)
                fontWeight(700); lineHeight("1.25"); textAlign("center")
            } }) { Text("Meet Devxer Pros") }
            P(attrs = { style {
                property("max-width", "760px"); property("margin", "0 auto 32px")
                fontFamily(Constants.FONT_FAMILY); fontSize(16.px); lineHeight("1.7"); textAlign("center")
            } }) { Text("Our conversational AI product brings voice, website chat, business knowledge and appointment workflows together. Explore how it works for immigration firms, dental practices and HVAC companies.") }
            Div(attrs = { style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fit, minmax(min(100%, 280px), 1fr))")
                property("gap", "24px"); property("align-items", "stretch")
            } }) { prosDemos.forEach { ProsDemoCard(it, palette) } }
            Div(attrs = { style { display(DisplayStyle.Flex); property("justify-content", "center"); property("margin-top", "32px") } }) {
                ProsLink("Explore Devxer Pros", "https://devxerpros.com/", palette)
            }
        }
    }
}

@Composable
private fun ProsDemoCard(demo: ProsDemo, palette: SitePalette) {
    Div(attrs = { style {
        display(DisplayStyle.Flex); property("flex-direction", "column"); property("min-width", "0")
        property("box-sizing", "border-box"); padding(24.px); borderRadius(8.px)
        backgroundColor(palette.softwareSectionBackground.rgb)
        property("border", "1px solid ${palette.commonButtonBorderColor.hex}")
    } }) {
        Img(src = demo.image, attrs = {
            attr("alt", demo.imageAlt); attr("loading", "lazy")
            attr("width", "585"); attr("height", "545")
            style {
                display(DisplayStyle.Block); width(100.percent); property("height", "auto")
                property("aspect-ratio", "585 / 545"); property("margin-bottom", "20px")
                borderRadius(8.px)
            }
        })
        H3(attrs = { style { margin(0.px, 0.px, 12.px); fontFamily(Constants.ROBOTO_FONT); fontSize(21.px); fontWeight(700); lineHeight("1.35") } }) { Text(demo.title) }
        P(attrs = { style { margin(0.px, 0.px, 24.px); property("flex", "1"); fontFamily(Constants.FONT_FAMILY); fontSize(16.px); lineHeight("1.7") } }) { Text(demo.description) }
        ProsLink("View Demo", demo.url, palette)
    }
}

@Composable
private fun ProsLink(label: String, url: String, palette: SitePalette) {
    var hovered by remember { mutableStateOf(false) }
    A(href = url, attrs = {
        attr("target", "_blank"); attr("rel", "noopener noreferrer")
        onMouseEnter { hovered = true }; onMouseLeave { hovered = false }
        style {
            property("display", "inline-flex"); property("align-items", "center"); property("justify-content", "center")
            property("align-self", "flex-start"); property("min-height", "48px"); property("box-sizing", "border-box")
            padding(12.px, 20.px); borderRadius(8.px)
            property("border", "1px solid ${palette.primary.hex}")
            backgroundColor(if (hovered) palette.commonSecondaryButtonColor.rgb else palette.primary.rgb)
            color(palette.bodyBackgroundSecondary.rgb)
            fontFamily(Constants.ROBOTO_FONT); fontSize(16.px); fontWeight(700); lineHeight("1.4"); textDecoration("none")
        }
    }) { Text(label) }
}

